import React, { useState, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import FlutedGlass from "./components/FlutedGlass.jsx";
import defaultBackgroundImage from "./assets/bruta-clean-compressed.png";

// Register GSAP plugin
gsap.registerPlugin(ScrollTrigger);

// Text styling configuration - shared between canvas and overlay
const TEXT_CONFIG = {
  fontFamily: '"Space Grotesk", sans-serif',
  fontWeight: 700,
  fontSize: 120, // Base size in px

  // color: "#000000", // Dark text for canvas
  // overlayColor: "#FFFFFF", // Light text for overlay
  color: "#FFFFFF",
  overlayColor: "#101005", // Dark text for overlay

  shadowBlur: 0,
  // shadowColor: "rgba(255, 255, 255, 0.99)",
  shadowColor: "rgba(0, 0, 0, 1)",

  hasStroke: false,

  overlayTextShadow: "0 0 20px rgba(255, 255, 255, 0.7)",
  // overlayTextShadow: "0 0 10px rgba(0, 0, 0, 0.2)",

  letterSpacing: "-0.05em",
};

// Scaling configuration
const SCALE_CONFIG = {
  minScale: 0.2, // Don't scale below 20% (prevents tiny unreadable text on mobile)
  maxScale: 1.0, // Don't upscale beyond native resolution
};

const ANIMATION_CONFIG = {
  phase1End: 0.05,
  phase2End: 1.05,
};

// Overlay clipping configuration
const CLIP_CONFIG = {
  edgeOffset: 4, // Offset in pixels: negative = reveal earlier, positive = reveal later
};

// Angle animation configuration
const ANGLE_CONFIG = {
  start: 140, // Starting angle (more vertical)
  end: 182, // Ending angle (horizontal)
};

// Function to create composite image with text on canvas
function createCompositeImage(imageSrc, text, textConfig = TEXT_CONFIG) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Don't use crossOrigin for local images (causes CORS issues)
    // Only set if loading from external domain
    if (imageSrc.startsWith("http") && !imageSrc.includes(window.location.hostname)) {
      img.crossOrigin = "anonymous";
    }

    img.onload = () => {
      try {
        // Get dimensions from the loaded image
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;

        // Create canvas at 2x resolution for sharper text rendering
        const scale = 1;
        const canvas2x = document.createElement("canvas");
        canvas2x.width = width * scale;
        canvas2x.height = height * scale;
        const ctx2x = canvas2x.getContext("2d");

        // Draw image at 2x size for sharp rendering
        ctx2x.drawImage(img, 0, 0, width * scale, height * scale);

        // Draw text at 2x using config
        ctx2x.font = `${textConfig.fontWeight} ${textConfig.fontSize * scale}px ${
          textConfig.fontFamily
        }`;
        ctx2x.fillStyle = textConfig.color;
        ctx2x.textAlign = "center";
        ctx2x.textBaseline = "middle";

        // Add text shadow
        ctx2x.shadowColor = textConfig.shadowColor;
        ctx2x.shadowBlur = textConfig.shadowBlur * scale;
        ctx2x.shadowOffsetX = 6 * scale;
        ctx2x.shadowOffsetY = 6 * scale;

        // Add text outline
        if (textConfig.hasStroke) {
          ctx2x.strokeStyle = textConfig.shadowColor;
          ctx2x.lineWidth = 6 * scale;
          ctx2x.strokeText(text, (width / 2) * scale, (height / 2) * scale);
        }

        ctx2x.fillText(text, (width / 2) * scale, (height / 2) * scale);

        // Create 1x canvas and downsample for optimal quality
        const canvas1x = document.createElement("canvas");
        canvas1x.width = width;
        canvas1x.height = height;
        const ctx1x = canvas1x.getContext("2d");

        // Downsample from 2x to 1x (sharp text, good performance)
        ctx1x.drawImage(canvas2x, 0, 0, width, height);

        // Convert to data URL and return with dimensions
        const dataUrl = canvas1x.toDataURL("image/png");
        resolve({ dataUrl, width, height });
      } catch (error) {
        console.error("Error creating composite image:", error);
        reject(error);
      }
    };

    img.onerror = (error) => {
      console.error("Error loading image:", imageSrc, error);
      reject(new Error(`Failed to load image: ${imageSrc}`));
    };

    img.src = imageSrc;
  });
}

// Wrapper component that manages the scroll-driven animation
function FlutedGlassAnimated({ text, imageSrc, overlayText, textConfig = TEXT_CONFIG }) {
  const [marginLeft, setMarginLeft] = useState(0);
  const [angle, setAngle] = useState(ANGLE_CONFIG.start);
  const [compositeData, setCompositeData] = useState(null);
  const [scale, setScale] = useState(1);
  const containerRef = useRef(null);

  // Create composite image on mount, waiting for fonts to load
  useEffect(() => {
    // Wait for fonts to load before rendering text
    document.fonts.ready.then(() => {
      createCompositeImage(imageSrc, text, textConfig)
        .then((data) => {
          setCompositeData(data);
        })
        .catch((error) => {
          console.error("Failed to create composite image:", error);
        });
    });
  }, [text, imageSrc, textConfig]);

  // Responsive scaling - maintain aspect ratio based on container width
  useEffect(() => {
    if (!compositeData || !containerRef.current) return;

    const updateScale = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerWidth = container.offsetWidth;
      const imageWidth = compositeData.width;
      const imageHeight = compositeData.height;

      // Calculate scale based purely on width (height will scale proportionally)
      const calculatedScale = containerWidth / imageWidth;

      // Clamp between min and max, maintaining aspect ratio
      const newScale = Math.max(
        SCALE_CONFIG.minScale,
        Math.min(SCALE_CONFIG.maxScale, calculatedScale)
      );

      setScale(newScale);
    };

    // Initial scale calculation
    updateScale();

    // Watch for container size changes
    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(containerRef.current);

    // Also listen to window resize as fallback
    window.addEventListener("resize", updateScale);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateScale);
    };
  }, [compositeData]);

  // GSAP ScrollTrigger animation with pinning
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !compositeData) {
      return;
    }

    // Animation phase configuration
    const PHASE_1_END = ANIMATION_CONFIG.phase1End; // When to start pinning (0-1)
    const PHASE_2_END = ANIMATION_CONFIG.phase2End; // When to end pinning (0-1)
    const PHASE_2_DURATION = PHASE_2_END - PHASE_1_END; // Duration of pinned phase

    // Calculate angle range from config
    const ANGLE_RANGE = ANGLE_CONFIG.end - ANGLE_CONFIG.start;

    // Phase 1: Normal scroll to center (0 to PHASE_1_END)
    ScrollTrigger.create({
      trigger: container,
      start: "top bottom-=25%",
      end: "center center",
      scrub: 0.5, // Reduced for better performance
      fastScrollEnd: true, // Stop updating during fast scrolls
      onUpdate: (self) => {
        const progress = self.progress * PHASE_1_END;
        setMarginLeft(progress);
        setAngle(ANGLE_CONFIG.start + progress * ANGLE_RANGE);
      },
      onLeave: () => {
        // Ensure we're at exact end state
        setMarginLeft(PHASE_1_END);
        setAngle(ANGLE_CONFIG.start + PHASE_1_END * ANGLE_RANGE);
      },
    });

    // Phase 2: Pinned in center (PHASE_1_END to PHASE_2_END)
    const phase2 = ScrollTrigger.create({
      trigger: container,
      start: "center center",
      end: "+=100%",
      pin: true,
      pinSpacing: true,
      scrub: 0.5, // Reduced for better performance
      fastScrollEnd: true,
      onUpdate: (self) => {
        const progress = PHASE_1_END + self.progress * PHASE_2_DURATION;
        setMarginLeft(progress);
        setAngle(ANGLE_CONFIG.start + progress * ANGLE_RANGE);
      },
      onLeave: () => {
        // Ensure we're at exact end state
        setMarginLeft(PHASE_2_END);
        setAngle(ANGLE_CONFIG.start + PHASE_2_END * ANGLE_RANGE);
      },
    });

    // Phase 3: Scroll away while completing (PHASE_2_END to 1.0)
    // Start at the exact scroll position where Phase 2 ends
    ScrollTrigger.create({
      trigger: container,
      start: () => phase2.end,
      end: () => phase2.end + container.offsetHeight,
      scrub: 0.5, // Reduced for better performance
      fastScrollEnd: true,
      onUpdate: (self) => {
        const progress = PHASE_2_END + self.progress * (1 - PHASE_2_END);
        setMarginLeft(progress);
        setAngle(ANGLE_CONFIG.start + progress * ANGLE_RANGE);
      },
      onLeave: () => {
        // Lock at final state when animation completes
        setMarginLeft(1);
        setAngle(ANGLE_CONFIG.end);
      },
      onLeaveBack: () => {
        setMarginLeft(PHASE_2_END);
        setAngle(ANGLE_CONFIG.start + PHASE_2_END * ANGLE_RANGE);
      },
    });

    // Cleanup
    return () => {
      ScrollTrigger.getAll().forEach((st) => {
        if (st.trigger === container) {
          st.kill();
        }
      });
    };
  }, [compositeData]); // Re-run when image is ready

  // Calculate scaled dimensions
  const scaledWidth = compositeData ? compositeData.width * scale : 0;
  const scaledHeight = compositeData ? compositeData.height * scale : 816;
  const scaledFontSize = (textConfig.fontSize + 10) * scale;

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        minHeight: `${scaledHeight}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* FlutedGlass with composite image (image + text "We send signal") */}
      {compositeData ? (
        <div
          style={{
            position: "relative",
            width: `${scaledWidth}px`,
            height: `${scaledHeight}px`,
          }}
        >
          {/* FlutedGlass scaled via CSS transform for quality */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              pointerEvents: "none",
            }}
          >
            <FlutedGlass
              marginLeft={marginLeft}
              angle={angle}
              imageUrl={compositeData.dataUrl}
              width={compositeData.width}
              height={compositeData.height}
            />
          </div>

          {/* Overlay text clipped to match glass movement, scaled proportionally */}
          <div
            style={{
              position: "absolute",
              top: "49%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 3,
              fontSize: `${scaledFontSize}px`,
              fontWeight: textConfig.fontWeight,
              fontFamily: textConfig.fontFamily,
              color: textConfig.overlayColor,
              textShadow: textConfig.overlayTextShadow,
              textAlign: "center",
              whiteSpace: "nowrap",
              letterSpacing: textConfig.letterSpacing,
              pointerEvents: "none",
              width: `${scaledWidth}px`,
              clipPath:
                CLIP_CONFIG.edgeOffset === 0
                  ? `inset(0 0 0 ${marginLeft * 100}%)`
                  : `inset(0 0 0 calc(${marginLeft * 100}% - ${
                      CLIP_CONFIG.edgeOffset
                    }px))`,
            }}
          >
            {overlayText}
          </div>
        </div>
      ) : (
        // Loading placeholder
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#666",
            fontSize: "1rem",
          }}
        >
          Loading...
        </div>
      )}
    </div>
  );
}

// Function to initialize and mount the React component
export function initFlutedGlass(options = {}) {
  const rootElement = document.getElementById("fluted-glass-root");

  if (!rootElement) {
    console.warn("FlutedGlass: Root element not found");
    return;
  }

  // Get configuration from options or data attributes
  const text = options.text || rootElement.dataset.text;
  const imageSrc =
    options.imageSrc || rootElement.dataset.imageSrc || defaultBackgroundImage;
  const overlayText = options.overlayText || rootElement.dataset.overlayText;

  // Require text and overlayText to be provided
  if (!text || !overlayText) {
    console.error(
      "FlutedGlass: text and overlayText are required. Provide via options or data-text/data-overlay-text attributes"
    );
    return;
  }

  try {
    const root = createRoot(rootElement);
    root.render(
      <FlutedGlassAnimated
        text={text}
        imageSrc={imageSrc}
        overlayText={overlayText}
        textConfig={options.textConfig}
      />
    );
  } catch (error) {
    console.error("FlutedGlass: Failed to render component", error);
    // Fallback: show the background image
    rootElement.innerHTML = `<img src="${imageSrc}" alt="Rocket recruiting visualization" style="width: 100%; height: auto; display: block;" />`;
  }
}

// Export configurations for customization
export { TEXT_CONFIG, SCALE_CONFIG, CLIP_CONFIG, ANGLE_CONFIG };
